import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import socketIOClient from 'socket.io-client';

import HistoricVoyageItem from '../../components/HistoricVoyageItem';
import MessageItem from '../../components/MessageItem';
import Nav from '../../components/Nav';
import NotificationItem from '../../components/NotificationItem';

import env from '../../config/env';
import '../../styles/styles.css';
import './mc-account-requests.css';

import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';

const socket = socketIOClient(env.apiUrl);

/**
 * Squelette affiché pendant le chargement des données.
 * Évite l'écran vide (ou le faux message "Aucune demande") avant l'arrivée des données.
 */
function ListSkeleton({ rows = 3 }) {
  return (
    <div className="mc-req-list" role="status" aria-busy="true">
      <span className="visually-hidden">Chargement en cours…</span>

      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="mc-req-skeleton-card" aria-hidden="true">
          <div className="mc-req-skeleton mc-req-skeleton--thumb"></div>

          <div className="mc-req-skeleton-body">
            <div className="mc-req-skeleton mc-req-skeleton--line mc-req-skeleton--w60"></div>
            <div className="mc-req-skeleton mc-req-skeleton--line mc-req-skeleton--w90"></div>
            <div className="mc-req-skeleton mc-req-skeleton--line mc-req-skeleton--w40"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AccountDemande() {
  const axios = useAxios();
  const { user, loading, isAuthenticated } = useUser();

  const [reservations, setReservations] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // États de chargement (true tant que les données ne sont pas arrivées)
  const [reservationsLoading, setReservationsLoading] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  // Onglet actif
  const [activeTab, setActiveTab] = useState('reserves');

  // ============================================================
  // Récupérer l'utilisateur connecté
  // ============================================================

  const getUserId = () => {
    return user?.id;
  };

  // ============================================================
  // Mode HOST
  // ============================================================

  useEffect(() => {
    localStorage.setItem('mode', 'host');
  }, []);

  // ============================================================
  // Récupération des réservations
  // (dépend de user?.id : avant, la requête partait une seule fois
  //  au montage, quand l'utilisateur n'était pas encore chargé)
  // ============================================================

  useEffect(() => {
    const fetchReservations = async () => {
      const userId = getUserId();

      if (!userId) {
        // Utilisateur encore en cours de chargement : on reste en "chargement".
        // Si le chargement du contexte est terminé et qu'il n'y a pas d'utilisateur,
        // il n'y a rien à attendre.
        if (!loading) setReservationsLoading(false);
        return;
      }

      setReservationsLoading(true);

      try {
        const response = await axios.get(`/reservations/getbydriverid/${userId}`);

        if (Array.isArray(response.data)) {
          const sortedData = [...response.data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          setReservations(sortedData);
        } else {
          setReservations([]);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des réservations :', error);
      } finally {
        setReservationsLoading(false);
      }
    };

    fetchReservations();
  }, [axios, user?.id, loading]);

  // ============================================================
  // Récupération des notifications
  // ============================================================

  useEffect(() => {
    const fetchNotifications = async () => {
      const userId = getUserId();

      if (!userId) {
        if (!loading) setNotificationsLoading(false);
        return;
      }

      setNotificationsLoading(true);

      try {
        const response = await axios.get(`/notifications/searchbyuserid/${userId}`);

        if (Array.isArray(response.data)) {
          const sortedData = [...response.data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          setNotifications(sortedData);
        } else {
          setNotifications([]);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des notifications :', error);
      } finally {
        setNotificationsLoading(false);
      }
    };

    fetchNotifications();
  }, [axios, user?.id, loading]);

  // ============================================================
  // Socket.IO
  // ============================================================

  useEffect(() => {
    const userId = getUserId();

    if (!userId) {
      return;
    }

    const handleConnect = () => {
      console.log('WebSocket Client Connected successfully');

      console.log('Socket ID:', socket.id);

      socket.emit('register', userId);
    };

    const handleNotification = (notification) => {
      console.log('Nouvelle notification reçue :', notification);

      setNotifications((prevNotifications) => [notification, ...prevNotifications]);
    };

    if (socket.connected) {
      handleConnect();
    }

    socket.on('connect', handleConnect);

    socket.on('notification', handleNotification);

    return () => {
      socket.off('connect', handleConnect);

      socket.off('notification', handleNotification);
    };
  }, [user?.id]);

  // ============================================================
  // Configuration des onglets
  // (pas de badge tant que les données ne sont pas chargées)
  // ============================================================

  const tabs = [
    {
      id: 'reserves',
      icon: 'bi-bell',
      label: 'Demandes',
      badge: reservationsLoading ? 0 : reservations.length,
    },
    {
      id: 'messages',
      icon: 'bi-chat-dots',
      label: 'Messages',
      badge: reservationsLoading ? 0 : reservations.length,
    },
    {
      id: 'notifications',
      icon: 'bi-megaphone',
      label: 'Notifs',
      badge: notificationsLoading ? 0 : notifications.length,
    },
  ];

  // ============================================================
  // Render
  // ============================================================

  return (
    <main className="mc-home mc-account-page">
      <div className="container mc-container">
        {/* =====================================================
            NAVIGATION
        ====================================================== */}

        <Nav />

        <div className="mc-account-shell">
          <div className="mc-account-head">
            <div>
              <h1 className="mc-account-title">Demandes</h1>
              <p className="mc-req-subtitle">
                Vos demandes de réservation, vos messages et vos notifications.
              </p>
            </div>
          </div>

          {/* =====================================================
              ONGLET NAVIGATION
          ====================================================== */}

          <div className="mc-req-tabs" role="tablist" aria-label="Sections">
            {tabs.map(({ id, icon, label, badge }) => {
              const isActive = activeTab === id;

              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`tab-${id}`}
                  aria-selected={isActive}
                  aria-controls={`panel-${id}`}
                  className={`mc-req-tab${isActive ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(id)}
                >
                  <i className={`bi ${icon}`} aria-hidden="true"></i>
                  <span className="mc-req-tab-label">{label}</span>
                  {badge > 0 && <span className="mc-req-tab-count">{badge}</span>}
                </button>
              );
            })}
          </div>

          {/* =====================================================
              CONTENU
          ====================================================== */}

          {/* ===================================================
              DEMANDES
          ==================================================== */}

          {activeTab === 'reserves' && (
            <section id="panel-reserves" role="tabpanel" aria-labelledby="tab-reserves">
              <div className="mc-req-section-head">
                <h2 className="mc-req-section-title">Réservations</h2>
                {!reservationsLoading && reservations.length > 0 && (
                  <span className="mc-req-section-count">{reservations.length}</span>
                )}
              </div>

              {reservationsLoading ? (
                <ListSkeleton />
              ) : reservations.length > 0 ? (
                <div className="mc-req-list">
                  {reservations.map((reservation) => (
                    <HistoricVoyageItem
                      key={reservation.reservationId}
                      annuler={true}
                      data={reservation}
                    />
                  ))}
                </div>
              ) : (
                <div className="mc-req-empty">
                  <span className="mc-req-empty-icon" aria-hidden="true">
                    <i className="bi bi-calendar-check"></i>
                  </span>

                  <h3>Pas de demandes de réservation</h3>

                  <p>
                    Vous n'avez aucune demande de réservation pour le moment. Pourquoi ne pas
                    perfectionner un peu votre annonce ?
                  </p>

                  <Link to="/account/voitures" className="mc-btn mc-btn--primary mc-btn--sm">
                    Gérer mes annonces
                  </Link>
                </div>
              )}
            </section>
          )}

          {/* ===================================================
              MESSAGES
          ==================================================== */}

          {activeTab === 'messages' && (
            <section id="panel-messages" role="tabpanel" aria-labelledby="tab-messages">
              <div className="mc-req-section-head">
                <h2 className="mc-req-section-title">Messages</h2>
                {!reservationsLoading && reservations.length > 0 && (
                  <span className="mc-req-section-count">{reservations.length}</span>
                )}
              </div>

              {reservationsLoading ? (
                <ListSkeleton />
              ) : reservations.length > 0 ? (
                <div className="mc-req-list">
                  {reservations.map((reservation) => (
                    <MessageItem key={reservation.reservationId} data={reservation} />
                  ))}
                </div>
              ) : (
                <div className="mc-req-empty">
                  <span className="mc-req-empty-icon" aria-hidden="true">
                    <i className="bi bi-chat-dots"></i>
                  </span>
                  <h3>Aucun message</h3>
                  <p>Aucun message pour le moment.</p>
                </div>
              )}
            </section>
          )}

          {/* ===================================================
              NOTIFICATIONS
          ==================================================== */}

          {activeTab === 'notifications' && (
            <section id="panel-notifications" role="tabpanel" aria-labelledby="tab-notifications">
              <div className="mc-req-section-head">
                <h2 className="mc-req-section-title">Notifications</h2>
                {!notificationsLoading && notifications.length > 0 && (
                  <span className="mc-req-section-count">{notifications.length}</span>
                )}
              </div>

              {notificationsLoading ? (
                <ListSkeleton rows={4} />
              ) : notifications.length > 0 ? (
                <div className="mc-req-list">
                  {notifications.map((notification, i) => (
                    <NotificationItem key={notification.id ?? i} data={notification} />
                  ))}
                </div>
              ) : (
                <div className="mc-req-empty">
                  <span className="mc-req-empty-icon" aria-hidden="true">
                    <i className="bi bi-bell"></i>
                  </span>
                  <h3>Aucune notification</h3>
                  <p>Aucune notification pour le moment.</p>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </main>
  );
}

export default AccountDemande;
